import { useEffect, useMemo, useState } from "react";
import { IoClose } from "react-icons/io5";
import DateRangeSelector from "../DateRangeSelector";
import { generateBankStatement } from "../../api/fetchers";
import { useSelector } from "react-redux";
import Loader from "../loader";
import NGNFlagImg from "asset/images/Currency.svg";
import GBPFlagImg from "asset/images/GB.svg";
import USFlagImg from "asset/images/US.png";
import EUFlagImg from "asset/images/EU.svg";
import USDCIcon from "asset/icons/USDC.svg";
import USDTIcon from "asset/icons/USDT.svg";

const CURRENCY_META = {
    NGN: { label: "Naira", icon: NGNFlagImg },
    USD: { label: "US Dollar", icon: USFlagImg },
    EUR: { label: "Euro", icon: EUFlagImg },
    GBP: { label: "Pound Sterling", icon: GBPFlagImg },
    USDC: { label: "USDC", icon: USDCIcon },
    USDT: { label: "USDT", icon: USDTIcon },
};

const GenerateStatementModal = ({
    show,
    onClose,
    wallet,
    email,
    setShowSuccessModal,
    setGeneratedData,
    setShowPinModal,
    setOnPinSuccess,
    isForFinance,
}) => {
    const [dateRange, setDateRange] = useState({ startDate: "", endDate: "" });
    const { business, currency: activeCurrency } = useSelector((state) => state);
    const [secureWithPassword, setSecureWithPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const currentBusinessId = business?._id;

    const currency = activeCurrency ?? "NGN";
    const meta = useMemo(() => CURRENCY_META[currency] ?? CURRENCY_META.NGN, [currency]);

    useEffect(() => {
        if (show) {
            setDateRange({ startDate: "", endDate: "" });
            setSecureWithPassword(false);
        }
    }, [show]);

    if (!show) return null;

    const handleDateRangeChange = ({ startDate, endDate }) => {
        setDateRange({ startDate, endDate });
    };

    const isFormValid = dateRange.startDate && dateRange.endDate;

    const setupPinForStatement = () => {
        setOnPinSuccess(() => async (pin) => {
            try {
                setIsLoading(true);
                const payload = {
                    currency,
                    fromDate: dateRange.startDate,
                    toDate: dateRange.endDate,
                    businessId: currentBusinessId,
                    isPassworded: secureWithPassword,
                    pin,
                };
                if (isForFinance) {
                    payload.isFinance = true;
                }
                await generateBankStatement(payload);

                setGeneratedData({
                    currency,
                    fromDate: dateRange.startDate,
                    toDate: dateRange.endDate,
                    businessId: currentBusinessId,
                    secureWithPassword,
                });
                setShowSuccessModal(true);
                setShowPinModal(false);
            } catch (error) {
                const apiError =
                    error?.response?.data?.message ||
                    error?.message ||
                    "Incorrect PIN. Please try again.";
                throw new Error(apiError);
            } finally {
                setIsLoading(false);
            }
        });

        onClose();
        setShowPinModal(true);
    };

    return (
        <div className="fixed inset-0 z-[800] flex items-center justify-center bg-black/30 px-2 md:px-4">
            <div className="relative w-full max-w-xl p-6 bg-white shadow-xl rounded-2xl">
                {/* Close Button */}
                <button onClick={onClose} className="absolute text-gray-500 top-4 right-4">
                    <IoClose size={24} />
                </button>

                {/* Title */}
                <h2 className="text-[24px] font-[600] text-[#161827]">Generate statement</h2>
                <p className="text-[16px] text-[#6F739B] font-[400] mt-1 mb-6">
                    Your statement will be sent to your registered email.
                </p>

                {/* Wallet Info */}
                {!isForFinance && (
                    <div className="mt-4 mb-6">
                        <label className="text-[14px] font-[450] text-[#383B56] mb-1 block">
                            Wallet
                        </label>
                        <div className="flex items-center justify-between px-4 py-3 border rounded-xl">
                            <div className="flex items-center gap-3">
                                <img
                                    src={meta.icon}
                                    alt={currency}
                                    className="w-10 h-10 rounded-full"
                                />
                                <div>
                                    <p className="text-[16px] font-semibold text-[#161827] leading-tight">
                                        {currency}
                                    </p>
                                    {/* <p className="text-[14px] text-[#6F739B] font-medium leading-tight mt-0.5">
                                    {wallet}
                                </p> */}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Date Range Presets */}
                <DateRangeSelector onChange={handleDateRangeChange} />

                {/* Email Info */}
                <div className="mt-4 mb-6">
                    <p className="text-sm font-medium text-[#161827] mb-1">
                        Registered email address
                    </p>
                    <input
                        type="text"
                        disabled
                        value={email}
                        className="w-full text-sm rounded-lg bg-[#EFF0FA] text-[#6F739B] px-3 py-3 cursor-not-allowed"
                    />
                </div>

                {/* Generate Button + Options */}
                <div className="border-t border-[#E5E7EB] pt-4 mt-6 flex items-center justify-between">
                    <label className="flex items-center space-x-3 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={secureWithPassword}
                            onChange={() => setSecureWithPassword(!secureWithPassword)}
                            className="w-4 h-4 text-[#6F00FF] bg-white border-gray-300 rounded focus:ring-[#6F00FF]"
                        />
                        <span className="text-sm text-[#161827] font-medium">
                            Secure with password
                        </span>
                    </label>

                    <button
                        className={`text-sm font-semibold px-3 py-2.5 rounded-lg transition-all duration-200 flex items-center justify-center gap-2 ${
                            !isFormValid || isLoading
                                ? "bg-[#D9CDFF] text-white cursor-not-allowed"
                                : "bg-[#6F00FF] text-white cursor-pointer"
                        }`}
                        disabled={!isFormValid || isLoading}
                        onClick={() => {
                            if (isFormValid && !isLoading) setupPinForStatement();
                        }}
                    >
                        {isLoading ? <Loader /> : "Generate"}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default GenerateStatementModal;
